import { dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeAdapterParams, TreeOptions, TreeSource } from "@omegagrid/tree";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { addDays, startOfISOWeek } from "date-fns";

const pad = (n: number, width: number) => n.toString().padStart(width, '0');
const range = (count: number) => [...new Array(count)].map((_, i) => i);
// 2020 is a leap year, so the day-of-year branch contains ****0229
const monthDate = (month: number, day = 1) => new Date(2020, month, day);
const daysInMonth = (month: number) => new Date(2020, month + 1, 0).getDate();

/**
 * Static tree of the common period ('PC') items - period types without a concrete year:
 * quarters ****1..****4, months ****01..****12, weeks ****001..****053,
 * days of year ****0101..****1231 and weekdays ********1..********7 (Monday = 1).
 * The Finance module has monthly granularity, so it only offers quarters and months.
 * Group nodes have no checkbox and are never serialized into the filter.
 */
export class CommonPeriodAdapter extends TreeAdapter {

	private items: NodeSource[];
	public accounting: number;

	constructor(
		private module: ModuleCode,
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(params: TreeAdapterParams): Promise<TreeSource> {
		if (this.items != null) return this.items;
		const monthly = this.module == 'F';

		this.items = [
			{k: 'Q', v: msg('Quarters'), ch: false, collapsed: true},
			...range(4).map(i => ({
				k: `****${i + 1}`,
				v: `${i + 1}. ${msg('Quarter')}`,
				l: 1
			})),

			{k: 'M', v: msg('Months'), ch: false, collapsed: true},
			...range(12).map(i => ({
				k: `****${pad(i + 1, 2)}`,
				v: dates.format(monthDate(i), 'LLLL'),
				l: 1
			})),

			...(monthly ? [] : [
				{k: 'W', v: msg('Weeks'), ch: false, collapsed: true},
				...range(53).map(i => ({
					k: `****${pad(i + 1, 3)}`,
					v: `${i + 1}. ${msg('Week')}`,
					l: 1
				})),

				{k: 'D', v: msg('Days of year'), ch: false, collapsed: true},
				...range(12).flatMap(m => [
					{k: `D${pad(m + 1, 2)}`, v: dates.format(monthDate(m), 'LLLL'), ch: false, collapsed: true, l: 1},
					...range(daysInMonth(m)).map(d => ({
						k: `****${pad(m + 1, 2)}${pad(d + 1, 2)}`,
						v: dates.format(monthDate(m, d + 1), 'd. M.'),
						l: 2
					})),
				]),

				{k: 'DW', v: msg('Weekdays'), ch: false, collapsed: true},
				...range(7).map(i => ({
					k: `********${i + 1}`,
					v: dates.format(addDays(startOfISOWeek(new Date()), i), 'EEEE'),
					l: 1
				})),
			]),
		];
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {
			selectMode: 'multiple',
			globalLevels: false
		};
	}

}
